import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  Bars3Icon,
  XMarkIcon,
  BookOpenIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline'
import HelpSidebar from '@/components/help/HelpSidebar'
import HelpContent from '@/components/help/HelpContent'
import ManualPdfGenerator from '@/components/help/ManualPdfGenerator'
import { manualChapters, MANUAL_VERSION, MANUAL_LAST_UPDATE } from '@/data/manual'
import { useTheme } from '@/contexts/ThemeContext'
import clsx from 'clsx'

export default function HelpPage() {
  const { chapterId, sectionId } = useParams()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { theme } = useTheme()

  // Redirect to first section if no section specified
  useEffect(() => {
    if (!chapterId && !sectionId) {
      // Show landing page
    } else if (chapterId && !sectionId) {
      const chapter = manualChapters.find(c => c.id === chapterId)
      if (chapter && chapter.sections.length > 0) {
        navigate(`/help/${chapterId}/${chapter.sections[0].id}`, { replace: true })
      }
    }
  }, [chapterId, sectionId, navigate])

  // Landing page when no chapter selected
  if (!chapterId) {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <header className="bg-white border-b sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-4">
                <Link
                  to="/dashboard"
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
                >
                  <ArrowLeftIcon className="h-5 w-5" />
                  <span className="hidden sm:inline">Torna alla piattaforma</span>
                </Link>
              </div>
              <div className="flex items-center gap-2">
                <BookOpenIcon className="h-6 w-6 text-primary-600" />
                <span className="font-semibold text-gray-900">Manuale Utente</span>
              </div>
              <div className="text-sm text-gray-500">
                v{MANUAL_VERSION}
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-slate-600 to-slate-800 text-white py-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <p className="text-slate-300 mb-2">{theme.companyName}</p>
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">{theme.appName}</h1>
            <p className="text-xl text-slate-200 mb-2">{theme.tagline}</p>
            <h2 className="text-2xl sm:text-3xl font-light mt-6 mb-8">Manuale Utente</h2>
            <ManualPdfGenerator className="inline-block" />
          </div>
        </section>

        {/* Chapters Grid */}
        <section className="max-w-6xl mx-auto px-4 py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Esplora il Manuale
          </h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {manualChapters.map((chapter) => (
              <Link
                key={chapter.id}
                to={`/help/${chapter.id}/${chapter.sections[0]?.id}`}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md hover:border-primary-300 transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600 font-bold text-lg group-hover:bg-primary-600 group-hover:text-white transition-colors">
                    {chapter.number}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                      {chapter.title}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {chapter.description}
                    </p>
                    <p className="text-xs text-gray-400 mt-2">
                      {chapter.sections.length} sezioni
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Quick Links */}
        <section className="bg-white border-t py-12">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">
              Accesso Rapido
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Link
                to="/help/getting-started/login"
                className="p-4 bg-green-50 rounded-lg border border-green-200 hover:bg-green-100 transition-colors text-center"
              >
                <span className="text-2xl">&#128640;</span>
                <p className="font-medium text-green-800 mt-2">Primo Accesso</p>
              </Link>
              <Link
                to="/help/forum/structure"
                className="p-4 bg-blue-50 rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors text-center"
              >
                <span className="text-2xl">&#128172;</span>
                <p className="font-medium text-blue-800 mt-2">Usare il Forum</p>
              </Link>
              <Link
                to="/help/courses/catalog"
                className="p-4 bg-purple-50 rounded-lg border border-purple-200 hover:bg-purple-100 transition-colors text-center"
              >
                <span className="text-2xl">&#127891;</span>
                <p className="font-medium text-purple-800 mt-2">Seguire i Corsi</p>
              </Link>
              <Link
                to="/help/appendix/faq"
                className="p-4 bg-orange-50 rounded-lg border border-orange-200 hover:bg-orange-100 transition-colors text-center"
              >
                <span className="text-2xl">&#10067;</span>
                <p className="font-medium text-orange-800 mt-2">FAQ</p>
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-100 border-t py-8">
          <div className="max-w-4xl mx-auto px-4 text-center text-sm text-gray-500">
            <p>
              Manuale Utente v{MANUAL_VERSION} - Ultimo aggiornamento: {MANUAL_LAST_UPDATE}
            </p>
            <p className="mt-2">
              {theme.copyright}
            </p>
          </div>
        </footer>
      </div>
    )
  }

  // Content page with sidebar
  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="flex items-center justify-between h-14 px-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 -ml-2 text-gray-500 hover:text-gray-700"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
            <Link
              to="/dashboard"
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 text-sm"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Piattaforma</span>
            </Link>
            <span className="text-gray-300">|</span>
            <Link to="/help" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
              <BookOpenIcon className="h-5 w-5 text-primary-600" />
              <span className="font-medium">Manuale</span>
            </Link>
          </div>
          <div className="hidden sm:block">
            <ManualPdfGenerator />
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Mobile sidebar overlay */}
        <div
          className={clsx(
            'fixed inset-0 z-50 lg:hidden',
            sidebarOpen ? 'block' : 'hidden'
          )}
        >
          <div
            className="fixed inset-0 bg-gray-600 bg-opacity-75"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 w-80 bg-white shadow-xl">
            <div className="flex items-center justify-between h-14 px-4 border-b">
              <span className="font-semibold">Indice</span>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-2 text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <HelpSidebar onSectionClick={() => setSidebarOpen(false)} />
          </div>
        </div>

        {/* Desktop sidebar */}
        <aside className="hidden lg:block w-80 border-r bg-gray-50 flex-shrink-0">
          <div className="sticky top-14 h-[calc(100vh-3.5rem)] overflow-hidden">
            <HelpSidebar />
          </div>
        </aside>

        {/* Content */}
        <main className="flex-1 min-w-0">
          <div className="p-6 lg:p-10">
            {chapterId && sectionId && (
              <HelpContent chapterId={chapterId} sectionId={sectionId} />
            )}
          </div>
        </main>
      </div>
    </div>
  )
}
